import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import StepIndicator from './components/StepIndicator';
import AccountDetailsStep from './components/AccountDetailsStep';
import GuildVerificationStep from './components/GuildVerificationStep';
import CharacterCreationStep from './components/CharacterCreationStep';
import ProgressSummary from './components/ProgressSummary';
import SuccessModal from './components/SuccessModal';

const AccountCreation = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    guildName: '',
    referralCode: '',
    characterName: '',
    characterClass: '',
    characterLevel: '',
    server: ''
  });
  const [errors, setErrors] = useState({});

  const steps = [
    { id: 1, title: 'Account Details', subtitle: 'Basic information' },
    { id: 2, title: 'Guild Verification', subtitle: 'Confirm membership' },
    { id: 3, title: 'Character Creation', subtitle: 'Add your character' }
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData?.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData?.username?.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    const correctGuildName = "Maladapted Miscreants";

    if (!formData?.guildName?.trim()) {
      newErrors.guildName = 'Guild name is required';
    } else if (formData?.guildName?.toLowerCase() !== correctGuildName?.toLowerCase()) {
      newErrors.guildName = 'Please enter the correct guild name: Maladapted Miscreants';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!formData?.characterName?.trim()) {
      newErrors.characterName = 'Character name is required';
    }

    if (!formData?.characterClass) {
      newErrors.characterClass = 'Please select a character class';
    }

    if (!formData?.characterLevel) {
      newErrors.characterLevel = 'Character level is required';
    } else if (formData?.characterLevel < 1 || formData?.characterLevel > 75) {
      newErrors.characterLevel = 'Level must be between 1 and 75';
    }

    if (!formData?.server) {
      newErrors.server = 'Please select a server';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    let isValid = false;

    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    } else if (currentStep === 3) {
      isValid = validateStep3();
    }

    if (isValid) {
      if (currentStep < steps?.length) {
        setCurrentStep(prev => prev + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    setShowSuccessModal(true);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <AccountDetailsStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
          />
        );
      case 2:
        return (
          <GuildVerificationStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
          />
        );
      case 3:
        return (
          <CharacterCreationStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            onSelectChange={handleSelectChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="text-center mb-8 md:mb-10 lg:mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
            Join Maladapted Miscreants
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Create your account and start tracking your progression through the Buried Sea expansion
          </p>
        </div>

        <StepIndicator currentStep={currentStep} steps={steps} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl p-6 md:p-8 lg:p-10 border border-border shadow-warm">
              {renderStepContent()}

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mt-6 md:mt-8">
                {currentStep > 1 && (
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="ArrowLeft"
                    iconPosition="left"
                    onClick={handlePrevious}
                    className="sm:w-auto"
                  >
                    Previous
                  </Button>
                )}
                
                <Button
                  variant="default"
                  size="lg"
                  iconName={currentStep === steps?.length ? 'Check' : 'ArrowRight'}
                  iconPosition="right"
                  onClick={handleNext}
                  fullWidth={currentStep === 1}
                  className={currentStep > 1 ? 'sm:flex-1' : ''}
                >
                  {currentStep === steps?.length ? 'Complete Registration' : 'Continue'}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <ProgressSummary
              currentStep={currentStep}
              totalSteps={steps?.length}
              formData={formData}
            />
          </div>
        </div>

        <div className="mt-8 md:mt-10 text-center">
          <p className="text-sm md:text-base text-muted-foreground">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/guild-dashboard')}
              className="text-primary hover:text-accent font-medium transition-colors"
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
      <SuccessModal isOpen={showSuccessModal} email={formData?.email} />
    </div>
  );
};

export default AccountCreation;